import { useCallback, useMemo, useRef } from 'react';
import {
  MAX_VIDEO_READY_WAIT_MS,
  MIN_READY_VIDEO_SECONDS,
  PLAYBACK_STATUS,
  VIDEO_READY_CHECK_INTERVAL_MS,
  VIDEO_START_FAILURE_TIMEOUT_MS,
} from '../config/videoPlayer.config';
import { hasEnoughVideoToStart } from '../utils/videoPlayer.utils';

/**
 * Starts playback after a stream source has just been attached or re-attached.
 *
 * The hook waits for enough loaded video before the first play attempt for that stream
 * setup. It is used for initial play, autoplay, and stream restarts, not for normal
 * resume after pause when the same stream is still attached.
 */
const useVideoStartup = ({
  isStillCurrentPlaybackRequest, // Function to check if the current playback request is still valid
  playbackStatusRef, // Ref to the current playback status (latest playback status, even if the component has re-rendered)
  restartStreamAfterPlaybackProblem, // Function from useVideoStreamRecovery to restart the stream after a playback problem has occurred
  getIsWatchTimeLimitReached, // Function that returns whether playback should stay blocked by the watch time limit
  setPlaybackStatus, // Setter to update the playback status (both ref and state)
  tryToPlayVideo, // Function to attempt to play the video
}) => {
  const pendingStartupRef = useRef(null); // Lets us cancel the delayed startup attempt before it runs
  const pendingReadyCheckRef = useRef(null); // Lets us cancel the next video-ready check before it runs
  const pendingStartupRecoveryRef = useRef(null); // Lets us cancel the delayed recovery fallback before it runs

  const cancelPendingStartup = useCallback(() => {
    [pendingStartupRef, pendingReadyCheckRef, pendingStartupRecoveryRef].forEach(
      (pendingDelayRef) => {
        if (pendingDelayRef.current) clearTimeout(pendingDelayRef.current);
        pendingDelayRef.current = null;
      },
    );
  }, []);

  const startPlayingWhenEnoughVideoIsReady = useCallback(
    (video, requestId, reason, delayMs) => {
      if (!video || !isStillCurrentPlaybackRequest(requestId)) return;

      cancelPendingStartup();
      setPlaybackStatus(PLAYBACK_STATUS.starting);

      pendingStartupRef.current = setTimeout(() => {
        const waitStartedAt = Date.now();

        const tryStartingVideo = () => {
          pendingReadyCheckRef.current = null;

          if (
            !isStillCurrentPlaybackRequest(requestId) ||
            getIsWatchTimeLimitReached() ||
            playbackStatusRef.current === PLAYBACK_STATUS.paused
          ) {
            return;
          }

          const waitedLongEnough = Date.now() - waitStartedAt >= MAX_VIDEO_READY_WAIT_MS;
          const hasEnoughVideo = hasEnoughVideoToStart(video, MIN_READY_VIDEO_SECONDS);

          if (!waitedLongEnough && !hasEnoughVideo) {
            pendingReadyCheckRef.current = setTimeout(
              tryStartingVideo,
              VIDEO_READY_CHECK_INTERVAL_MS,
            );
            return;
          }

          tryToPlayVideo(video, requestId, reason);

          // Lets the useVideoStreamRecovery hook take over to handle any playback problems after a short delay
          pendingStartupRecoveryRef.current = setTimeout(() => {
            pendingStartupRecoveryRef.current = null;

            if (
              isStillCurrentPlaybackRequest(requestId) &&
              !getIsWatchTimeLimitReached() &&
              playbackStatusRef.current === PLAYBACK_STATUS.starting
            ) {
              restartStreamAfterPlaybackProblem();
            }
          }, VIDEO_START_FAILURE_TIMEOUT_MS);
        };

        tryStartingVideo();
      }, delayMs);
    },
    [
      cancelPendingStartup,
      getIsWatchTimeLimitReached,
      isStillCurrentPlaybackRequest,
      playbackStatusRef,
      restartStreamAfterPlaybackProblem,
      setPlaybackStatus,
      tryToPlayVideo,
    ],
  );

  return useMemo(
    () => ({
      cancelPendingStartup,
      startPlayingWhenEnoughVideoIsReady,
    }),
    [cancelPendingStartup, startPlayingWhenEnoughVideoIsReady],
  );
};

export default useVideoStartup;
